import { ExternalLink, Mail, Phone, Reply, Tag } from "lucide-react";
import { cn } from "@/lib/utils";

const formatDate = (iso) => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("en-US", {
    timeZone: "America/New_York",
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const Badge = ({ ok, yes, no }) => (
  <span
    className={cn(
      "rounded-full px-2.5 py-1 font-mono text-xs",
      ok ? "bg-emerald-400/10 text-emerald-300" : "bg-white/[0.05] text-[#A1A1AA]",
    )}
  >
    {ok ? yes : no}
  </span>
);

const CardShell = ({ testId, title, subtitle, date, children, item, replyHref }) => (
  <article className="rounded-2xl border border-white/[0.08] bg-[#0E1015] p-5 sm:p-6" data-testid={testId}>
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0">
        <h3 className="break-words text-lg font-medium tracking-tight">{title}</h3>
        {subtitle && <p className="mt-0.5 text-sm text-[#A1A1AA]">{subtitle}</p>}
      </div>
      <time className="font-mono text-xs text-[#A1A1AA]" dateTime={date}>
        {formatDate(date)}
      </time>
    </div>
    {children}
    <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-white/[0.06] pt-4">
      <div className="flex flex-wrap gap-2">
        <Badge ok={item.email_sent} yes="Emailed to inbox" no="Inbox email not sent" />
        <Badge ok={item.confirmation_sent} yes="Thank-you sent" no="No thank-you sent" />
      </div>
      <a
        href={replyHref}
        className="inline-flex items-center gap-1.5 text-sm font-medium text-white transition-colors hover:text-[#2DD4D4]"
        data-testid={`${testId}-reply`}
      >
        <Reply className="h-4 w-4" aria-hidden="true" /> Reply
      </a>
    </div>
  </article>
);

const mailto = (email, subject) => `mailto:${email}?subject=${encodeURIComponent(subject)}`;

export const ContactCard = ({ item }) => (
  <CardShell
    testId={`contact-submission-${item.id}`}
    title={item.name}
    subtitle={item.business}
    date={item.created_at}
    item={item}
    replyHref={mailto(item.email, "Re: your message to ATA Forge")}
  >
    {item.topic && (
      <p className="mt-4 inline-flex items-center gap-2 rounded-full border border-[#2F6BFF]/40 bg-[#2F6BFF]/10 px-3 py-1 text-xs text-white">
        <Tag className="h-3 w-3 text-[#2DD4D4]" aria-hidden="true" /> {item.topic}
      </p>
    )}
    <p className="mt-4 whitespace-pre-wrap break-words text-[15px] leading-relaxed text-white/85">{item.message}</p>
    <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-sm text-white/85">
      <a href={`mailto:${item.email}`} className="inline-flex items-center gap-2 break-all hover:text-white">
        <Mail className="h-4 w-4 shrink-0 text-[#2F6BFF]" aria-hidden="true" /> {item.email}
      </a>
      {item.phone && (
        <a href={`tel:${item.phone.replace(/[^\d+]/g, "")}`} className="inline-flex items-center gap-2 hover:text-white">
          <Phone className="h-4 w-4 shrink-0 text-[#2F6BFF]" aria-hidden="true" /> {item.phone}
        </a>
      )}
    </div>
  </CardShell>
);

export const CheckCard = ({ item }) => (
  <CardShell
    testId={`website-check-${item.id}`}
    title={item.url.replace(/^https?:\/\//i, "")}
    subtitle="Free website check request"
    date={item.created_at}
    item={item}
    replyHref={mailto(item.email, "Your free website check from ATA Forge")}
  >
    <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-sm text-white/85">
      <a href={item.url} target="_blank" rel="noopener noreferrer nofollow" className="inline-flex items-center gap-2 break-all hover:text-white">
        <ExternalLink className="h-4 w-4 shrink-0 text-[#2F6BFF]" aria-hidden="true" /> Open website
      </a>
      <a href={`mailto:${item.email}`} className="inline-flex items-center gap-2 break-all hover:text-white">
        <Mail className="h-4 w-4 shrink-0 text-[#2F6BFF]" aria-hidden="true" /> {item.email}
      </a>
    </div>
  </CardShell>
);
